import { useRef, useState } from 'react';
import {
  deposerPieceFormateur,
  genererCvAnonyme,
  modifierPieceFormateur,
  supprimerPieceFormateur,
  urlPieceFormateur,
} from '../services/formateurService';
import { formatDate } from './devisUtils';
import { FORMATS_ACCEPTES } from './categoriesDocument';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import NomEditable from './NomEditable';
import VignetteFichier from '../components/VignetteFichier';
import '../assets/styles/Drive.css';

/**
 * Pièces administratives d'un formateur (migrations 080 et 083) : CV,
 * attestation URSSAF, assurance RC Pro, diplôme, permis de conduire, notation
 * des stagiaires chez un autre organisme. L'URSSAF et la RC Pro portent une
 * date d'échéance : l'équipe est prévenue avant (délai réglé dans
 * Paramétrage > Rappels) pour redemander une attestation au formateur.
 *
 * Le CV anonyme ne se dépose pas : il est produit à chaque dépôt de CV, et
 * se refait ici à la demande.
 */
export const TYPES_PIECE = [
  { value: 'cv', label: 'CV', echeance: false },
  { value: 'urssaf', label: 'Attestation URSSAF', echeance: true },
  { value: 'rc_pro', label: 'Assurance RC Pro', echeance: true },
  { value: 'diplome', label: 'Diplôme', echeance: false },
  { value: 'permis', label: 'Permis de conduire', echeance: false },
  { value: 'carte_grise', label: 'Carte grise', echeance: false },
  { value: 'notation_of', label: 'Notation stagiaires (autre organisme)', echeance: false },
  { value: 'autre', label: 'Autre pièce', echeance: false },
];

const aujourdhuiIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Les deux attestations qui portent une échéance. */
export const ATTESTATIONS = [
  { type: 'urssaf', libelle: 'Attestation URSSAF' },
  { type: 'rc_pro', libelle: 'Assurance RC Pro' },
];

/**
 * Les attestations dont l'échéance est atteinte (aujourd'hui ou dépassée).
 * On juge la plus lointaine de chaque nature : une attestation renouvelée
 * efface l'alerte de l'ancienne. Accepte les pièces de la fiche
 * (`dateEcheance`) comme le résumé de la liste (`echeance`).
 */
export function attestationsEchues(pieces = []) {
  const auj = aujourdhuiIso();
  return ATTESTATIONS.flatMap(({ type, libelle }) => {
    const dates = (pieces || [])
      .filter((p) => p.type === type)
      .map((p) => String(p.dateEcheance || p.echeance || '').slice(0, 10))
      .filter(Boolean)
      .sort();
    const derniere = dates[dates.length - 1];
    return derniere && derniere <= auj ? [{ type, libelle, echeance: derniere }] : [];
  });
}

/**
 * Le bandeau rouge d'une attestation arrivée à échéance : sur la ligne du
 * formateur dans la liste, et en tête de sa fiche.
 */
export function AlerteAttestations({ echues }) {
  if (!echues || echues.length === 0) return null;
  return (
    <div className="formateur-alerte" role="alert">
      <span className="formateur-alerte-icone" aria-hidden="true">
        !
      </span>
      <div>
        <div className="formateur-alerte-titre">
          {echues.length > 1 ? 'Attestations à renouveler' : 'Attestation à renouveler'}
        </div>
        {echues.map((a) => (
          <div key={a.type}>
            {a.libelle} — échéance atteinte le {formatDate(a.echeance)}
          </div>
        ))}
      </div>
    </div>
  );
}

/** L'état d'une échéance : échue, proche (30 jours), à jour. */
export function etatEcheance(dateEcheance) {
  if (!dateEcheance) return null;
  const jour = String(dateEcheance).slice(0, 10);
  const auj = aujourdhuiIso();
  if (jour < auj) return { libelle: `échue depuis le ${formatDate(jour)}`, classe: ' devis-pill-refuse' };
  const dans30 = new Date();
  dans30.setDate(dans30.getDate() + 30);
  const limite = dans30.toISOString().slice(0, 10);
  if (jour <= limite) return { libelle: `échéance le ${formatDate(jour)}`, classe: ' devis-pill-attente' };
  return { libelle: `valable jusqu’au ${formatDate(jour)}`, classe: ' devis-pill-valide' };
}

const FORM_VIDE = { type: 'cv', dateEcheance: '', libelle: '' };

/* ------------------------------------------------------------------ */
/* Tuiles — le même dessin que les documents des clients (ClientDrive) */
/* ------------------------------------------------------------------ */

/** Feuille commune à toutes les icônes : coin corné, couleur et sigle. */
function IconeFichier({ variante, sigle }) {
  return (
    <svg
      className={`drive-icone drive-icone-${variante}`}
      viewBox="0 0 40 48"
      width="40"
      height="48"
      aria-hidden="true"
    >
      <path
        d="M4 4a3 3 0 0 1 3-3h17l13 13v30a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V4z"
        className="drive-icone-page"
      />
      <path d="M24 1l13 13H27a3 3 0 0 1-3-3V1z" className="drive-icone-corne" />
      <rect x="4" y="26" width="33" height="14" rx="3" className="drive-icone-bandeau" />
      <text x="20.5" y="36.5" textAnchor="middle" className="drive-icone-sigle">
        {sigle}
      </text>
    </svg>
  );
}

/** Le type MIME décide de la couleur et du sigle. */
export function iconePour(mime) {
  const type = String(mime || '');
  if (type === 'application/pdf') return <IconeFichier variante="pdf" sigle="PDF" />;
  if (type.startsWith('image/')) return <IconeFichier variante="image" sigle="IMG" />;
  if (type.includes('word')) return <IconeFichier variante="doc" sigle="DOC" />;
  return <IconeFichier variante="autre" sigle="FIC" />;
}

/**
 * Une pièce du dossier : l'aperçu du fichier, son libellé renommable, son
 * état d'échéance, et les gestes — ouvrir, télécharger, renommer, corriger
 * l'échéance, retirer.
 */
function TuilePiece({ piece, onRenommer, onEcheance, onSupprimer, occupe }) {
  const [renommage, setRenommage] = useState(false);
  // null = fermé ; sinon la valeur en cours de saisie.
  const [echeanceEdit, setEcheanceEdit] = useState(null);
  const aEcheance = TYPES_PIECE.find((t) => t.value === piece.type)?.echeance;
  const etat =
    etatEcheance(piece.dateEcheance) ||
    (aEcheance ? { libelle: 'échéance à compléter', classe: ' devis-pill-attente' } : null);
  const ouvrir = () => window.open(urlPieceFormateur(piece.id, { apercu: true }), '_blank');

  return (
    <article className="drive-tuile">
      {/* L'aperçu du fichier lui-même : on reconnaît la pièce d'un coup
          d'œil. Cliquer l'ouvre. */}
      <div
        className="drive-tuile-visuel drive-tuile-visuel-apercu"
        role="button"
        tabIndex={0}
        title="Ouvrir"
        onClick={ouvrir}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            ouvrir();
          }
        }}
      >
        <VignetteFichier
          url={urlPieceFormateur(piece.id, { apercu: true })}
          mime={piece.mime}
          alt={`Aperçu de ${piece.libelle || piece.nomFichier}`}
          secours={iconePour(piece.mime)}
          largeur={232}
        />
      </div>

      <div className="drive-tuile-corps">
        <div className="drive-tuile-nom" title={piece.libelle || piece.nomFichier}>
          <NomEditable
            valeur={piece.libelle || piece.typeLibelle}
            edition={renommage}
            onRenommer={(nom) => onRenommer(piece, nom)}
            onFermer={() => setRenommage(false)}
          />
        </div>
        <div className="drive-tuile-meta" title={piece.nomFichier}>
          {piece.nomFichier}
        </div>
        <div className="drive-tuile-meta">
          {piece.typeLibelle}
          {etat && (
            <span className={`devis-status-pill${etat.classe}`} style={{ marginLeft: 6 }}>
              {etat.libelle}
            </span>
          )}
        </div>
        <div className="drive-tuile-meta">
          déposée le {formatDate(piece.deposeLe)}
          {piece.alerteEnvoyeeLe ? ` · alerte le ${formatDate(piece.alerteEnvoyeeLe)}` : ''}
        </div>

        {echeanceEdit !== null && (
          <div className="devis-form-row" style={{ alignItems: 'flex-end', marginTop: 4, gap: 6 }}>
            <label className="devis-form-field">
              <span>Échéance</span>
              <input
                type="date"
                value={echeanceEdit}
                onChange={(e) => setEcheanceEdit(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="devis-btn devis-btn-sm devis-btn-primary"
              disabled={occupe}
              onClick={async () => {
                await onEcheance(piece, echeanceEdit);
                setEcheanceEdit(null);
              }}
            >
              OK
            </button>
            <button
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setEcheanceEdit(null)}
            >
              Annuler
            </button>
          </div>
        )}
      </div>

      <div className="drive-tuile-actions">
        <button type="button" className="drive-action" onClick={ouvrir} title="Ouvrir la pièce">
          Ouvrir
        </button>
        <a className="drive-action" href={urlPieceFormateur(piece.id)} download title="Télécharger">
          Télécharger
        </a>
        <button
          type="button"
          className="drive-action"
          onClick={() => setRenommage(true)}
          disabled={renommage || occupe}
          title="Renommer la pièce"
        >
          Renommer
        </button>
        {aEcheance && (
          <button
            type="button"
            className="drive-action"
            onClick={() => setEcheanceEdit(String(piece.dateEcheance || '').slice(0, 10))}
            disabled={occupe}
            title="Corriger la date d’échéance"
          >
            Échéance
          </button>
        )}
        <button
          type="button"
          className="drive-action drive-action-danger"
          onClick={() => onSupprimer(piece)}
          disabled={occupe}
          title="Retirer du dossier"
        >
          Retirer
        </button>
      </div>
    </article>
  );
}

/**
 * Les pièces que l'on attend de chaque formateur, dans l'ordre où on les
 * demande. Une ligne sans fichier propose [Déposer] : c'est le raccourci vers
 * le formulaire de dépôt, déjà réglé sur la bonne nature.
 */
export const PIECES_ATTENDUES = [
  { type: 'cv', libelle: 'CV' },
  { type: 'urssaf', libelle: 'Attestation URSSAF' },
  { type: 'rc_pro', libelle: 'Assurance RC Pro' },
  { type: 'diplome', libelle: 'Diplôme' },
  { type: 'permis', libelle: 'Permis de conduire' },
  { type: 'carte_grise', libelle: 'Carte grise' },
];

/**
 * Le formulaire de dépôt : nature, échéance (attestations), libellé, fichier.
 * Il ne s'ouvre que sur [Déposer un fichier] ou sur le [Déposer] d'une ligne
 * dont le fichier manque — `typeInitial` règle alors la nature.
 */
export function DepotPieceModal({ formateurId, typeInitial = 'cv', onClose, onDepose }) {
  const [form, setForm] = useState({ ...FORM_VIDE, type: typeInitial });
  const [fichiers, setFichiers] = useState([]);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [depot, setDepot] = useState(null);
  // Chaque fichier avance de lui-même pendant son envoi : à 0, la barre ne
  // s'afficherait pas du tout pendant le premier.
  const avanceDepot = useProgression(depot ? depot.fait + 1 : 0, {
    estimeeMs: depot && depot.type === 'cv' ? 12000 : 3000,
  });
  const fichierRef = useRef(null);
  const typeChoisi = TYPES_PIECE.find((t) => t.value === form.type);

  async function deposer(e) {
    e.preventDefault();
    if (!fichiers.length) {
      setErreur('Choisissez le fichier à déposer.');
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      // Plusieurs fichiers (diplômes) : un dépôt chacun. Une échéance vide
      // est lue dans l'attestation par le serveur.
      let sansEcheance = false;
      let cvAnonymeManque = null;
      for (const [i, fichier] of fichiers.entries()) {
        setDepot({ fait: i, total: fichiers.length, nom: fichier.name, type: form.type });
        const res = await deposerPieceFormateur(formateurId, { ...form, fichier });
        if (typeChoisi?.echeance && !res.data?.dateEcheance) sansEcheance = true;
        if (res.data?.cvAnonyme && !res.data.cvAnonyme.genere) cvAnonymeManque = res.data.cvAnonyme.raison;
      }
      onDepose({
        avertissement: cvAnonymeManque
          ? `CV déposé, mais CV anonyme non généré : ${cvAnonymeManque}`
          : sansEcheance
            ? 'Échéance introuvable dans le fichier : corrigez-la au crayon.'
            : null,
      });
    } catch (err) {
      setErreur(err.message || 'Dépôt impossible.');
      setEnCours(false);
      setDepot(null);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={enCours ? undefined : onClose}>
      <div className="param-modal" style={{ maxWidth: 560, height: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title">Déposer un fichier</h2>
          <button type="button" className="param-modal-close" onClick={onClose} disabled={enCours} aria-label="Fermer">
            ×
          </button>
        </div>
        <form className="param-modal-body" onSubmit={deposer}>
          <label className="devis-form-field">
            <span>Nature</span>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} disabled={enCours}>
              {TYPES_PIECE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          {typeChoisi?.echeance && (
            <label className="devis-form-field">
              <span>Échéance (facultatif, lue dans le fichier)</span>
              <input
                type="date"
                value={form.dateEcheance}
                onChange={(e) => setForm({ ...form, dateEcheance: e.target.value })}
                disabled={enCours}
              />
            </label>
          )}
          <label className="devis-form-field">
            <span>Libellé (facultatif)</span>
            <input
              type="text"
              value={form.libelle}
              placeholder={typeChoisi?.label}
              onChange={(e) => setForm({ ...form, libelle: e.target.value })}
              disabled={enCours}
            />
          </label>
          <label className="devis-form-field">
            <span>Fichier</span>
            <input
              ref={fichierRef}
              type="file"
              accept={FORMATS_ACCEPTES}
              multiple={form.type === 'diplome' || form.type === 'autre'}
              onChange={(e) => setFichiers(Array.from(e.target.files || []))}
              disabled={enCours}
            />
          </label>

          {depot && (
            <BarreProgression
              valeur={((depot.fait + avanceDepot / 100) / depot.total) * 100}
              libelle={`Dépôt : ${depot.nom} (${depot.fait + 1}/${depot.total})${
                depot.type === 'cv' ? ' — génération du CV anonyme' : ''
              }`}
              estimation
            />
          )}
          {erreur && <div className="devis-row-erreur">{erreur}</div>}

          <div className="devis-modal-actions" style={{ marginTop: 16 }}>
            <button type="button" className="devis-btn" onClick={onClose} disabled={enCours}>
              Annuler
            </button>
            <button type="submit" className="devis-btn devis-btn-primary" disabled={enCours}>
              {enCours ? 'Envoi…' : 'Déposer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Onglet « Pièces administratives » : ce qui est attendu (avec [Déposer] sur
 * ce qui manque), puis les pièces au dossier. Le formulaire de dépôt n'existe
 * que dans la fenêtre : `onDeposer(type)` demande au parent de l'ouvrir.
 */
function PiecesFormateur({ formateurId, pieces, onChange, onDeposer }) {
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [generation, setGeneration] = useState(false);
  const progressionCv = useProgression(generation, { estimeeMs: 25000 });

  const aUnCv = pieces.some((p) => p.type === 'cv');
  const aUnCvAnonyme = pieces.some((p) => p.type === 'cv_anonyme');

  // Recherche et filtre par nature, sur la grille des pièces.
  const [recherche, setRecherche] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const simplifie = (t) =>
    String(t || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const mots = simplifie(recherche).split(/\s+/).filter(Boolean);
  const piecesAffichees = pieces.filter((p) => {
    if (filtreType && p.type !== filtreType) return false;
    if (!mots.length) return true;
    const texte = simplifie([p.libelle, p.typeLibelle, p.nomFichier].join(' '));
    return mots.every((m) => texte.includes(m));
  });

  /** Refait le CV anonyme à partir du dernier CV au dossier. */
  async function refaireCvAnonyme() {
    setGeneration(true);
    setErreur(null);
    try {
      await genererCvAnonyme(formateurId);
      onChange();
    } catch (err) {
      setErreur(err.message || 'Génération du CV anonyme impossible.');
    } finally {
      setGeneration(false);
    }
  }

  /** Renomme la pièce (NomEditable affiche lui-même l'erreur éventuelle). */
  async function renommer(piece, nom) {
    await modifierPieceFormateur(piece.id, { libelle: nom });
    onChange();
  }

  /** Corrige la date d'échéance d'une attestation ; l'alerte repart à zéro. */
  async function changerEcheance(piece, date) {
    setErreur(null);
    try {
      await modifierPieceFormateur(piece.id, { dateEcheance: date });
      onChange();
    } catch (err) {
      setErreur(err.message || 'Modification impossible.');
    }
  }

  async function supprimer() {
    setEnCours(true);
    try {
      await supprimerPieceFormateur(suppression.id);
      setSuppression(null);
      onChange();
    } catch (err) {
      setErreur(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="devis-card clients-demande">
      <div className="clients-section">
        <div className="ff-section-tete">
          <div className="clients-section-titre">Pièces attendues</div>
          <button type="button" className="devis-btn devis-btn-primary" onClick={() => onDeposer('cv')}>
            Déposer un fichier
          </button>
        </div>
        {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}
        <div className="ff-attendues">
          {PIECES_ATTENDUES.map(({ type, libelle }) => {
            const deposees = pieces.filter((p) => p.type === type);
            return (
              <div className={`ff-attendue${deposees.length ? ' ff-attendue-ok' : ''}`} key={type}>
                <span className="ff-attendue-etat" aria-hidden="true">
                  {deposees.length ? '✓' : '!'}
                </span>
                <span className="ff-attendue-texte">
                  <span className="ff-attendue-nom">{libelle}</span>
                  <span className="ff-attendue-detail">
                    {deposees.length
                      ? `${deposees.length} fichier${deposees.length > 1 ? 's' : ''} au dossier`
                      : 'Fichier manquant'}
                  </span>
                </span>
                {deposees.length === 0 ? (
                  <button type="button" className="devis-btn devis-btn-sm devis-btn-primary" onClick={() => onDeposer(type)}>
                    Déposer
                  </button>
                ) : (
                  <button type="button" className="devis-btn devis-btn-sm" onClick={() => onDeposer(type)}>
                    Ajouter
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="clients-section">
        <div className="clients-section-titre">Pièces au dossier</div>
        {pieces.length > 0 && (
          <div className="devis-toolbar" style={{ marginTop: 4, marginBottom: 10 }}>
            <input
              type="search"
              className="devis-search"
              placeholder="Rechercher une pièce (libellé, fichier...)"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
            />
            <select
              className="drive-filtre"
              value={filtreType}
              onChange={(e) => setFiltreType(e.target.value)}
              aria-label="Filtrer par nature"
            >
              <option value="">Toutes les natures</option>
              {TYPES_PIECE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
              <option value="cv_anonyme">CV anonyme</option>
            </select>
          </div>
        )}
        {aUnCv && (
          <div className="clients-ligne">
            <div className="clients-ligne-texte">
              <div>CV anonyme</div>
              <div className="clients-ligne-detail">
                Le CV sans nom ni coordonnées, à joindre à une proposition. Produit à chaque dépôt
                de CV.
              </div>
            </div>
            <div className="clients-ligne-actions">
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={refaireCvAnonyme}
                disabled={enCours || generation}
              >
                {generation ? 'Génération…' : aUnCvAnonyme ? 'Régénérer' : 'Générer'}
              </button>
            </div>
          </div>
        )}
        <BarreProgression valeur={progressionCv} libelle="Génération du CV anonyme par l’IA" estimation />
        {pieces.length === 0 && (
          <div className="clients-ligne-detail">
            Aucune pièce : CV, attestation URSSAF, assurance RC Pro, diplôme…
          </div>
        )}
        {pieces.length > 0 && piecesAffichees.length === 0 && (
          <div className="clients-ligne-detail">Aucune pièce ne correspond.</div>
        )}
        {piecesAffichees.length > 0 && (
          <div className="drive-grille">
            {piecesAffichees.map((p) => (
              <TuilePiece
                key={p.id}
                piece={p}
                onRenommer={renommer}
                onEcheance={changerEcheance}
                onSupprimer={setSuppression}
                occupe={enCours}
              />
            ))}
          </div>
        )}
      </div>

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer cette pièce ?"
          sousTitre={`${suppression.typeLibelle} · ${suppression.nomFichier}`}
          details={['Le fichier et sa date d’échéance.']}
          enCours={enCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={supprimer}
        />
      )}
    </div>
  );
}

export default PiecesFormateur;
